/**
 * Plateau Detection System
 * 
 * Automatically detects when user's progress has stalled and identifies likely causes
 * Uses statistical analysis to determine if lack of progress is significant
 */

import { linearRegression, mean, variance } from 'simple-statistics';
import type { SessionLog } from './sessionStorage';
import { getExerciseHistory } from './sessionStorage';
import { computeSetVolume } from './volumeTracking';
import { getRecoveryHistory } from './recoveryCalculator';

export interface PlateauDetection {
  exerciseName: string;
  plateauDetected: boolean;
  weeksSinceProgress: number;
  lastProgressDate: string;
  currentWeight: number;
  peakWeight: number;
  
  // Statistical confidence
  confidence: 'low' | 'medium' | 'high';
  
  // Likely causes (ranked by probability)
  likelyCauses: Array<{
    cause: 'insufficient_volume' | 'insufficient_recovery' | 'technique_breakdown' | 
           'accumulated_fatigue' | 'need_variation' | 'overtraining' | 'undertraining';
    probability: number; // 0-1
    evidence: string;
  }>;
  
  // Recommended interventions
  interventions: Array<{
    type: 'deload' | 'volume_increase' | 'technique_focus' | 'exercise_variation' | 
          'frequency_adjustment' | 'intensity_adjustment';
    description: string;
    expectedDuration: string; // "1-2 weeks"
    priority: 'high' | 'medium' | 'low';
  }>;
}

// Exercises to exclude from plateau detection (warmup, recovery, mobility, bodyweight core)
const EXCLUDED_FROM_PLATEAU = new Set([
  // Warmup/Mobility
  'foam roll', 'foam rolling', 'stretch', 'stretching', 'mobility', 'warmup', 'warm up', 'warm-up',
  'dynamic warmup', 'dynamic warm-up', 'dynamic warm up', 'dynamic',
  'band pull apart', 'band pull-apart', 'arm circles', 'leg swings', 'hip circles',
  'cat cow', 'cat-cow', 'world\'s greatest stretch', 'dynamic stretch',
  // Recovery/Flexibility
  'yoga', 'static stretch', 'cool down', 'cooldown', 'recovery',
  // Bodyweight core (no weight progression)
  'plank', 'side plank', 'dead bug', 'bird dog', 'hollow hold', 'hollow body',
  'mountain climber', 'flutter kick', 'leg raise', 'lying leg raise',
  'crunch', 'sit up', 'sit-up', 'bicycle crunch', 'russian twist',
  // Light activation work
  'glute bridge', 'hip thrust bodyweight', 'clamshell', 'fire hydrant',
  'band walks', 'monster walks', 'x-band walk',
  // Cardio (not strength progression)
  'walking', 'jogging', 'running', 'cycling', 'rowing', 'elliptical', 'stairmaster',
  'jump rope', 'jumping jacks', 'burpee', 'burpees',
]);

/**
 * Check if exercise should be excluded from plateau detection
 */
export function shouldExcludeFromPlateau(exerciseName: string): boolean {
  const normalized = exerciseName.toLowerCase().trim();

  // Direct match
  if (EXCLUDED_FROM_PLATEAU.has(normalized)) return true;

  // Partial match for common patterns
  for (const excluded of Array.from(EXCLUDED_FROM_PLATEAU)) {
    if (normalized.includes(excluded) || excluded.includes(normalized)) {
      return true;
    }
  }

  // Exclude anything with these keywords
  const excludeKeywords = ['warmup', 'warm up', 'warm-up', 'stretch', 'mobility', 'recovery', 'foam', 'band walk', 'dynamic'];
  for (const keyword of excludeKeywords) {
    if (normalized.includes(keyword)) return true;
  }

  return false;
}

/**
 * Detect plateaus across all exercises
 */
export async function detectPlateaus(sessions: SessionLog[]): Promise<PlateauDetection[]> {
  const plateaus: PlateauDetection[] = [];

  // Get unique exercises
  const exerciseNames = new Set<string>();
  sessions.forEach(session => {
    if (session.sessionType === 'pt' && session.exercises) {
      session.exercises.forEach(ex => {
        // Skip warmup, recovery, and bodyweight core exercises
        if (!shouldExcludeFromPlateau(ex.exerciseName)) {
          exerciseNames.add(ex.exerciseName);
        }
      });
    }
  });

  // Check each exercise for plateau
  for (const exerciseName of Array.from(exerciseNames)) {
    const detection = await detectExercisePlateau(exerciseName, sessions);
    if (detection.plateauDetected) {
      plateaus.push(detection);
    }
  }

  return plateaus;
}

/**
 * Detect plateau for a specific exercise
 */
export async function detectExercisePlateau(
  exerciseName: string,
  sessions: SessionLog[]
): Promise<PlateauDetection> {
  // Extract exercise data
  const exerciseData: Array<{
    date: string;
    weight: number;
    reps: number;
    rpe: number;
    volume: number;
  }> = [];
  
  sessions.forEach(session => {
    if (session.sessionType === 'pt' && session.exercises) {
      const exercise = session.exercises.find(ex => 
        ex.exerciseName.toLowerCase() === exerciseName.toLowerCase()
      );
      
      if (exercise && exercise.sets && exercise.sets.length > 0) {
        // Get best set (highest weight × reps)
        const bestSet = exercise.sets.reduce((best, set) => {
          if (!set.weight || !set.reps) return best;
          const score = computeSetVolume(set, exerciseName);
          const bestScore = best ? computeSetVolume(best, exerciseName) : 0;
          return score > bestScore ? set : best;
        }, exercise.sets[0]);
        
        if (bestSet.weight && bestSet.reps) {
          const volume = exercise.sets.reduce((sum, set) =>
            sum + computeSetVolume(set, exerciseName), 0
          );
          
          exerciseData.push({
            date: typeof session.date === 'string' ? session.date : session.date.toISOString(),
            weight: bestSet.weight,
            reps: bestSet.reps,
            rpe: bestSet.rpe || 7,
            volume,
          });
        }
      }
    }
  });
  
  // Need at least 6 data points to detect plateau
  if (exerciseData.length < 6) {
    return {
      exerciseName,
      plateauDetected: false,
      weeksSinceProgress: 0,
      lastProgressDate: '',
      currentWeight: 0,
      peakWeight: 0,
      confidence: 'low',
      likelyCauses: [],
      interventions: [],
    };
  }
  
  // Sort by date
  exerciseData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  
  // Find peak weight and when it occurred
  const peakWeight = Math.max(...exerciseData.map(d => d.weight));
  const peakIndex = exerciseData.findIndex(d => d.weight === peakWeight);
  const peakDate = exerciseData[peakIndex].date;
  
  // Get current (most recent) weight
  const currentWeight = exerciseData[exerciseData.length - 1].weight;
  
  // Calculate weeks since peak
  const weeksSincePeak = (new Date().getTime() - new Date(peakDate).getTime()) / (7 * 24 * 60 * 60 * 1000);
  
  // Get last 4 sessions
  const recentSessions = exerciseData.slice(-4);
  const recentWeights = recentSessions.map(d => d.weight);
  
  // Calculate trend in recent sessions
  const recentTrend = linearRegression(
    recentSessions.map((d, i) => [i, d.weight])
  );
  const recentSlope = recentTrend.m;
  
  // Plateau criteria:
  // 1. No progress in 3+ weeks
  // 2. Recent trend is flat or negative (slope < 0.5 lbs/session)
  // 3. Current weight < peak weight OR no improvement in 4 sessions
  const isStagnant = recentSlope < 0.5;
  const hasRegressed = currentWeight < peakWeight * 0.95; // 5% regression
  const noRecentProgress = Math.max(...recentWeights) <= peakWeight;
  
  const plateauDetected = weeksSincePeak >= 3 && isStagnant && (hasRegressed || noRecentProgress);
  
  if (!plateauDetected) {
    return {
      exerciseName,
      plateauDetected: false,
      weeksSinceProgress: Math.floor(weeksSincePeak),
      lastProgressDate: peakDate,
      currentWeight,
      peakWeight,
      confidence: 'low',
      likelyCauses: [],
      interventions: [],
    };
  }
  
  // Analyze likely causes
  const likelyCauses = await analyzePlateauCauses(exerciseData, sessions);
  
  // Generate interventions
  const interventions = generateInterventions(likelyCauses, exerciseData);
  
  // Calculate confidence based on data quality
  const confidence = calculatePlateauConfidence(exerciseData, weeksSincePeak);
  
  return {
    exerciseName,
    plateauDetected: true,
    weeksSinceProgress: Math.floor(weeksSincePeak),
    lastProgressDate: peakDate,
    currentWeight,
    peakWeight,
    confidence,
    likelyCauses,
    interventions,
  };
}

/**
 * Analyze likely causes of plateau
 */
async function analyzePlateauCauses(
  exerciseData: Array<{ date: string; weight: number; reps: number; rpe: number; volume: number }>,
  allSessions: SessionLog[]
): Promise<PlateauDetection['likelyCauses']> {
  const causes: PlateauDetection['likelyCauses'] = [];
  
  // Get recent data (last 4 sessions)
  const recentData = exerciseData.slice(-4);
  const avgRecentRPE = mean(recentData.map(d => d.rpe));
  const avgRecentVolume = mean(recentData.map(d => d.volume));
  
  // Get historical data (sessions 5-8 from end)
  const historicalData = exerciseData.slice(-8, -4);
  const avgHistoricalVolume = historicalData.length > 0 ? mean(historicalData.map(d => d.volume)) : avgRecentVolume;
  
  // 1. Check for insufficient volume
  if (avgRecentVolume < avgHistoricalVolume * 0.9) {
    causes.push({
      cause: 'insufficient_volume',
      probability: 0.8,
      evidence: `Volume decreased ${Math.round(((avgHistoricalVolume - avgRecentVolume) / avgHistoricalVolume) * 100)}% from previous period`,
    });
  }
  
  // 2. Check for technique breakdown (high RPE but no progress)
  if (avgRecentRPE >= 8.5) {
    causes.push({
      cause: 'technique_breakdown',
      probability: 0.7,
      evidence: `High RPE (${avgRecentRPE.toFixed(1)}) suggests form may be breaking down`,
    });
  }
  
  // 3. Check for accumulated fatigue
  const recoveryData = await getRecoveryHistory();
  const recentRecovery = recoveryData.slice(-7); // Last week
  if (recentRecovery.length >= 3) {
    const avgReadiness = mean(recentRecovery.map(r => {
      const readiness = (r.sleepHours / 10) * 30 + (r.energy / 10) * 40 + ((10 - r.overallSoreness) / 10) * 30;
      return readiness;
    }));
    
    if (avgReadiness < 50) {
      causes.push({
        cause: 'accumulated_fatigue',
        probability: 0.85,
        evidence: `Low average readiness (${avgReadiness.toFixed(0)}/100) indicates accumulated fatigue`,
      });
    }
  }
  
  // 4. Check for overtraining (high RPE + low recovery)
  if (avgRecentRPE >= 8 && recentRecovery.length >= 3) {
    const avgReadiness = mean(recentRecovery.map(r => {
      return (r.sleepHours / 10) * 30 + (r.energy / 10) * 40 + ((10 - r.overallSoreness) / 10) * 30;
    }));
    
    if (avgReadiness < 60) {
      causes.push({
        cause: 'overtraining',
        probability: 0.75,
        evidence: `High RPE (${avgRecentRPE.toFixed(1)}) + low readiness (${avgReadiness.toFixed(0)}) = overtraining`,
      });
    }
  }
  
  // 5. Check for undertraining (low RPE but no progress)
  if (avgRecentRPE < 6.5) {
    causes.push({
      cause: 'undertraining',
      probability: 0.6,
      evidence: `Low RPE (${avgRecentRPE.toFixed(1)}) suggests insufficient intensity`,
    });
  }
  
  // 6. Check for need of variation (long time on same exercise)
  if (exerciseData.length > 12) {
    causes.push({
      cause: 'need_variation',
      probability: 0.5,
      evidence: `${exerciseData.length} sessions on same exercise - may benefit from variation`,
    });
  }
  
  // 7. Check for insufficient recovery between sessions
  const sessionDates = exerciseData.slice(-4).map(d => new Date(d.date));
  const daysBetweenSessions = sessionDates.slice(1).map((date, i) => 
    (date.getTime() - sessionDates[i].getTime()) / (24 * 60 * 60 * 1000)
  );
  const avgDaysBetween = mean(daysBetweenSessions);
  
  if (avgDaysBetween < 2 && avgRecentRPE >= 8) {
    causes.push({
      cause: 'insufficient_recovery',
      probability: 0.7,
      evidence: `Only ${avgDaysBetween.toFixed(1)} days between sessions - may need more recovery`,
    });
  }
  
  // Sort by probability (highest first)
  causes.sort((a, b) => b.probability - a.probability);
  
  return causes.slice(0, 3); // Return top 3 causes
}

/**
 * Generate intervention recommendations
 */
function generateInterventions(
  causes: PlateauDetection['likelyCauses'],
  exerciseData: Array<{ date: string; weight: number; reps: number; rpe: number; volume: number }>
): PlateauDetection['interventions'] {
  const interventions: PlateauDetection['interventions'] = [];
  
  // Get top cause
  const topCause = causes[0];
  if (!topCause) return interventions;
  
  switch (topCause.cause) {
    case 'accumulated_fatigue':
    case 'overtraining':
      interventions.push({
        type: 'deload',
        description: 'Take a deload week: reduce volume by 40-50% and intensity by 10-15%',
        expectedDuration: '1 week',
        priority: 'high',
      });
      interventions.push({
        type: 'frequency_adjustment',
        description: 'Increase rest days between sessions from current frequency',
        expectedDuration: '2-3 weeks',
        priority: 'medium',
      });
      break;
      
    case 'insufficient_volume':
      interventions.push({
        type: 'volume_increase',
        description: 'Add 1-2 sets per exercise or add a variation exercise',
        expectedDuration: '3-4 weeks',
        priority: 'high',
      });
      break;
      
    case 'technique_breakdown':
      interventions.push({
        type: 'technique_focus',
        description: 'Reduce weight by 10-15% and focus on perfect form for 2 weeks',
        expectedDuration: '2 weeks',
        priority: 'high',
      });
      interventions.push({
        type: 'intensity_adjustment',
        description: 'Keep RPE at 6-7 until movement quality improves',
        expectedDuration: '2-3 weeks',
        priority: 'medium',
      });
      break;
      
    case 'undertraining':
      interventions.push({
        type: 'intensity_adjustment',
        description: 'Increase intensity: target RPE 7-8 instead of current level',
        expectedDuration: '2-3 weeks',
        priority: 'high',
      });
      break;
      
    case 'need_variation':
      interventions.push({
        type: 'exercise_variation',
        description: 'Substitute with a variation (e.g., front squat instead of back squat) for 3-4 weeks',
        expectedDuration: '3-4 weeks',
        priority: 'medium',
      });
      break;
      
    case 'insufficient_recovery':
      interventions.push({
        type: 'frequency_adjustment',
        description: 'Add 1 extra rest day between sessions for this exercise',
        expectedDuration: '2-3 weeks',
        priority: 'high',
      });
      break;
  }
  
  // Always add a general recommendation
  if (interventions.length === 0 || topCause.probability < 0.6) {
    interventions.push({
      type: 'deload',
      description: 'Try a deload week as a general reset strategy',
      expectedDuration: '1 week',
      priority: 'medium',
    });
  }
  
  return interventions;
}

/**
 * Calculate confidence in plateau detection
 */
function calculatePlateauConfidence(
  exerciseData: Array<{ date: string; weight: number; reps: number; rpe: number; volume: number }>,
  weeksSincePeak: number
): 'low' | 'medium' | 'high' {
  // More data = higher confidence
  const dataConfidence = Math.min(exerciseData.length / 12, 1.0);

  // More time since peak = higher confidence
  const timeConfidence = Math.min(weeksSincePeak / 6, 1.0);

  // Check consistency (low variance = higher confidence)
  const recentWeights = exerciseData.slice(-4).map(d => d.weight);
  const weightVariance = variance(recentWeights);
  const consistencyConfidence = weightVariance < 25 ? 1.0 : weightVariance < 100 ? 0.7 : 0.4;

  const overallConfidence = (dataConfidence * 0.4) + (timeConfidence * 0.3) + (consistencyConfidence * 0.3);

  if (overallConfidence >= 0.7) return 'high';
  if (overallConfidence >= 0.4) return 'medium';
  return 'low';
}

// ============================================================================
// LIGHTWEIGHT PLATEAU CHECK (for per-exercise use in smartWorkoutGenerator)
// ============================================================================

export interface SimplePlateauResult {
  isPlateaued: boolean;
  sessionsSinceProgress: number;
  suggestion: string;
}

/**
 * Lightweight plateau detection for a single exercise.
 * Queries session history internally — no need to pass sessions.
 * Used by smartWorkoutGenerator during per-exercise processing.
 */
export async function detectPlateau(exerciseName: string): Promise<SimplePlateauResult> {
  try {
    if (shouldExcludeFromPlateau(exerciseName)) {
      return { isPlateaued: false, sessionsSinceProgress: 0, suggestion: '' };
    }

    const history = await getExerciseHistory(exerciseName);

    // Need at least 4 sessions (3 stall + 1 reference)
    if (history.length < 4) {
      return { isPlateaued: false, sessionsSinceProgress: 0, suggestion: '' };
    }

    // Take last 6 sessions, most recent first
    const recent = history.slice(-6).reverse();

    const sessionData = recent.map(session => {
      const sets = session.sets || [];
      const maxWeight = Math.max(...sets.map(s => s.weight || 0), 0);
      const maxReps = Math.max(...sets.map(s => s.reps || 0), 0);
      return { maxWeight, maxReps };
    });

    // Count consecutive sessions without progress from most recent
    let stallCount = 0;
    const baseline = sessionData[0];

    for (let i = 1; i < sessionData.length; i++) {
      const prev = sessionData[i];
      const weightProgressed = baseline.maxWeight > prev.maxWeight;
      const repsProgressed = baseline.maxWeight >= prev.maxWeight && baseline.maxReps > prev.maxReps;

      if (weightProgressed || repsProgressed) break;
      stallCount++;
    }

    if (stallCount < 3) {
      return { isPlateaued: false, sessionsSinceProgress: stallCount, suggestion: '' };
    }

    // Graduated suggestions
    let suggestion: string;
    if (stallCount >= 5) {
      suggestion = 'Try a variation (e.g., pause reps, tempo change, or alternate grip)';
    } else if (stallCount >= 4) {
      suggestion = 'Reduce weight 10% and rebuild over 3 weeks';
    } else {
      suggestion = 'Consider a deload week before pushing again';
    }

    return { isPlateaued: true, sessionsSinceProgress: stallCount, suggestion };
  } catch (error) {
    console.error(`Lightweight plateau check failed for ${exerciseName}:`, error);
    return { isPlateaued: false, sessionsSinceProgress: 0, suggestion: '' };
  }
}

